import { useEffect, useMemo, useState } from "react";
import { Form, Col, Row } from "antd";
import useGet from "../../../global/hooks/useGet";
import CustomInput from "../../../global/components/inputWidgets/CustomInput";
import OptionCards from "../../../global/components/inputWidgets/OptionCards";
import { useNavigate, useParams } from "react-router-dom";
import { openErrorNotification } from "../../../global/utils/toast";
import {
  DesktopOutlined,
  InfoCircleOutlined,
  SettingOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import {
  formatUnixTime,
  formatUTCTime,
} from "../../../global/utils/unixToLocalTime";
import DeviceLogIcon from "../../../assets/images/vectors/SideBarIcons/DeviceLogIcon";
import CommonButton from "../../../global/components/CommonButton";
import CustomSelect from "../../../global/components/inputWidgets/CustomSelect";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import {
  getItemNoByLabel,
  HIDDEN_OPTION_NAMES,
  LICENSE_QUANTITY_FIELDS,
  toVisibleOptionList,
} from "../../../utils/Common";

const visibleOptionNames = (options) =>
  String(options ?? "")
    .split(",")
    .map((name) => name.trim())
    .filter((name) => name !== "" && !HIDDEN_OPTION_NAMES.includes(name))
    .join(", ");

export const DeviceLogInfo = () => {
  const [form] = Form.useForm();
  const { id } = useParams();
  const navigate = useNavigate();

  const url = `${process.env.REACT_APP_API_URL}/api/devicelogs/${id}/`;
  const optionUrl = `${process.env.REACT_APP_API_URL}/api/devices/options/`;

  const {
    data: optionsData,
    error: optionDataError,
    refetch: optionDataRefetch,
  } = useGet(optionUrl);

  const { data, error, refetch, loading } = useGet(url);
  const modifiedSiteTypes = getModifiedResponse(optionsData, "sitetype");
  const modifiedModels = getModifiedResponse(optionsData, "models");
  // Same option tiles as the Device Info page.
  const modifiedOp = useMemo(
    () => toVisibleOptionList(optionsData?.data?.options),
    [optionsData],
  );
  const [licenseOptionsData, setLicenseOptionsData] = useState({});

  // The log API sends Options as a name string (confirmed format) — the
  // OptionCards tiles are highlighted by matching those names.
  useEffect(() => {
    if (!data?.data) return;
    const state = {};

    visibleOptionNames(data?.data?.Options)
      .split(",")
      .map((name) => name.trim())
      .filter(Boolean)
      .forEach((name) => {
        const option = modifiedOp.find(
          (opt) => opt.label.toLowerCase() === name.toLowerCase(),
        );
        if (option) {
          state[option.label] = {
            type: "toggle",
            enabled: true,
            value: option.value,
            itemNo: getItemNoByLabel(option.label),
          };
        }
      });

    LICENSE_QUANTITY_FIELDS.forEach(({ label, fieldName, type }) => {
      const value = data.data[fieldName];
      if (value && value > 0) {
        state[label] = {
          type,
          quantity: fieldName === "Invitation" ? value * 100 : value,
          fieldName,
          value: modifiedOp.find((opt) => opt.label === label)?.value || null,
        };
      }
    });
    setLicenseOptionsData(state);
  }, [data, modifiedOp]);

  useEffect(() => {
    const getDeviceInfoById = () => {
      try {
        if (data?.data) {
          form.setFieldsValue({
            LogTime: data?.data?.LogTime
              ? formatUTCTime(data?.data?.LogTime)
              : "",
            // AccountNo: data?.data?.AccountName
            //   ? String(data?.data?.AccountName)
            //   : "",
            Account: data?.data?.Account,
            Oem: data?.data?.Oem,
            // Production: data?.data?.Production,
            Dealer: data?.data?.Dealer,
            Customer: data?.data?.Customer,
            NodeType: data?.data?.NodeType,
            DeviceName: data?.data?.DeviceName,
            Mac: data?.data?.Mac,
            Product: data?.data?.Product,
            Model: data?.data?.Model,
            SiteType: data?.data?.SiteType,
            Type: String(data?.data?.Type),
            OemNo: data?.data?.OemName,
            Options: visibleOptionNames(data?.data?.Options),
            Camera:
              data?.data?.Camera != null ? String(data?.data?.Camera) : "",
            Channel:
              data?.data?.Channel != null ? String(data?.data?.Channel) : "",
            Lockset:
              data?.data?.Lockset != null ? String(data?.data?.Lockset) : "",
            Facegate:
              data?.data?.Facegate != null ? String(data?.data?.Facegate) : "",
            Subnode:
              data?.data?.Subnode != null ? String(data?.data?.Subnode) : "",
            ContLock:
              data?.data?.ContLock != null ? String(data?.data?.ContLock) : "",
            Intercom:
              data?.data?.Intercom != null && Number(data.data.Intercom) > 0
                ? String(data.data.Intercom)
                : 0,
            Sicucom: data.data.Sicucom != null ? String(data.data.Sicucom) : "",
            Invitation: data.data.Invitation ? data.data.Invitation * 100 : 0,
            LicenseKey: data?.data?.LicenseKey
              ? String(data?.data?.LicenseKey)
              : "",
            Expired: data?.data?.Expired === 0 ? "No" : "Yes",
          });
        } else {
          console.warn("No data found for the given ID");
        }
      } catch (error) {
        console.log({ error });
        openErrorNotification("Error fetching data!");
      }
    };

    getDeviceInfoById();
  }, [id, data, form]);

  const breadcrumbItems = [
    {
      label: "Device Log",
      href: "/devicelog",
    },
    {
      label: "Information",
      href: `/devicelog/info/${id}`,
    },
  ];
  const buttonsData = [
    {
      label: "List",
      icon: <UnorderedListOutlined />,
      onClick: () => {
        navigate(-1);
      },
    },
  ];

  return (
    <div>
      {/* <CommonBreadcrumb breadcrumbItems={breadcrumbItems} /> */}
      <div className="pb-4 sticky -mt-4 pt-[6px] top-[55px] z-10 bg-[#f4f5f9]">
        <CommonBreadcrumb breadcrumbItems={breadcrumbItems}>
          <CommonButton buttonsData={buttonsData} />
        </CommonBreadcrumb>
      </div>

      <div className="">
        <Form
          validateTrigger={"onChange"}
          form={form}
          layout={"vertical"}
          autoComplete={"off"}
        >
          <div className="bg-white border rounded-[4px] overflow-hidden">
            <div className="flex items-center bg-gray-200 p-2.5">
              <DeviceLogIcon className="text-[18px] text-black " />
              <span className="text-[.935rem] text-black font-['Poppins'] ml-2">
                Device
              </span>
            </div>
            <div className="p-2">
              <Row gutter={[16, 8]}>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Log Time"
                    name="LogTime"
                    disabled={true}
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Account Name"
                    name="Account"
                    disabled={true}
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Device Name"
                    name="DeviceName"
                    disabled={true}
                    loading={loading}
                  />
                </Col>
                {/* <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Production"
                    name="Production"
                    disabled={true}
                  />
                </Col> */}
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="OEM"
                    name="Oem"
                    disabled={true}
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Dealer"
                    name="Dealer"
                    disabled={true}
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Customer"
                    name="Customer"
                    disabled={true}
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="NodeType"
                    name="NodeType"
                    disabled={true}
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomSelect
                    label="Site Type"
                    name="SiteType"
                    options={modifiedSiteTypes}
                    placeholder="Please select"
                    disabled={true}
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="MAC"
                    name="Mac"
                    disabled={true}
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Product"
                    name="Product"
                    disabled={true}
                    loading={loading}
                  />
                </Col>
              </Row>
              {/* <Row gutter={[16, 16]}>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput label="Expired" name="Expired" disabled={true} />
                </Col>
              </Row> */}
            </div>
          </div>
          <div className="mt-4 bg-white border rounded-[4px] overflow-hidden">
            <div className="flex items-center bg-gray-200 p-2.5">
              <InfoCircleOutlined className="text-[18px] text-black " />
              <span className="text-[.935rem] text-black font-['Poppins'] ml-2">
                License Information
              </span>
            </div>
            <div className="p-2">
              <Row gutter={[16, 8]}>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomSelect
                    label="Model"
                    name="Model"
                    options={modifiedModels}
                    placeholder="Please select"
                    disabled={true}
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Door"
                    name="Type"
                    type="number"
                    disabled={true}
                    loading={loading}
                  />
                </Col>
              </Row>
              {/* <Row gutter={[16, 16]}> */}
              {/* <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput label="Type" name="Type" disabled={true} />
                </Col> */}

              {/* <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput label="OEM" name="OemNo" disabled={true} />
                </Col> */}
              {/* </Row> */}
            </div>
          </div>
          <div className="mt-4 bg-white border rounded-[4px] overflow-hidden">
            <div className="flex items-center bg-gray-200 p-2.5">
              <SettingOutlined className="text-[18px] text-black " />
              <span className="text-[.935rem] text-black font-['Poppins'] ml-2">
                License Options
              </span>
            </div>
            <div className="p-3">
              <OptionCards
                options={modifiedOp}
                disabled
                loading={loading}
                value={licenseOptionsData}
                onChange={setLicenseOptionsData}
                hidePrices
              />
            </div>
          </div>
          {/* <div className="mt-4 bg-white border rounded-[4px] overflow-hidden">
            <div className="flex items-center bg-gray-200 p-2.5">
              <DesktopOutlined className="text-[18px] text-black " />
              <span className="text-[.935rem] text-black font-['Poppins'] ml-2">
                License Devices
              </span>
            </div>
            <div className="p-2">
              <Row gutter={[16, 8]}>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Channel"
                    name="Channel"
                    disabled={true}
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Lockset"
                    name="Lockset"
                    disabled={true}
                    loading={loading}
                  />
                </Col>

                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="OSDP Door"
                    name="Subnode"
                    disabled={true}
                    loading={loading}
                  />
                </Col>

                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Trilogy Lockset"
                    name="ContLock"
                    disabled={true}
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Unit"
                    name="Sicucom"
                    disabled={true}
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Mobile Credential"
                    name="Invitation"
                    disabled={true}
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Landline"
                    name="Intercom"
                    disabled={true}
                    loading={loading}
                  />
                </Col>
              </Row>
            </div>
          </div> */}
        </Form>
      </div>
    </div>
  );
};
